import { Component, Input, SimpleChanges, OnChanges, Output, EventEmitter } from '@angular/core';
import { FlatTreeControl} from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import { SelectionModel } from '@angular/cdk/collections';
import { IMapTypes } from 'src/app/dashboard/map-settings/settings';


/** Flat node with expandable and level information */
export class FlatTreeNode {
  expandable: boolean;
  name: string;
  level: number;
  isSelected :boolean;
  isImplemented : boolean;
}

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss']
})
export class MultiSelectComponent implements OnChanges {

  @Input() sourceData:IMapTypes;
  @Output() selectedItems = new EventEmitter<any>();
  
  getLevel = (node: FlatTreeNode) => node.level;
  isExpandable = (node: FlatTreeNode) => node.expandable;
  getChildren = (node: IMapTypes): IMapTypes[] => node.children;
  hasChild = (_: number, _nodeData: FlatTreeNode) => _nodeData.expandable;

  treeControl: FlatTreeControl<FlatTreeNode>;
  treeFlattener: MatTreeFlattener<IMapTypes, FlatTreeNode>;
  dataSource: MatTreeFlatDataSource<IMapTypes, FlatTreeNode>;
  flatItemArray: FlatTreeNode[] = [];

  /** The selection for checklist */
  checklistSelection = new SelectionModel<FlatTreeNode>(true /* multiple */);
   
   private transformer = (node: IMapTypes, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level: level,
      isSelected :node.isSelected,
      isImplemented : node.isImplemented
    };

  }

  constructor() {
      this.treeFlattener = new MatTreeFlattener(
        this.transformer,
        this.getLevel,
        this.isExpandable,
        this.getChildren);
      this.treeControl = new FlatTreeControl<FlatTreeNode>(this.getLevel, this.isExpandable);
      this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    }
  ngOnChanges(): void {
      const data = Array.isArray(this.sourceData) ? this.sourceData : [this.sourceData];
      this.dataSource.data = data;
  }
  ngOnInit() {
    // Pre-select checkboxes based on `isSelected` for every node
    this.dataSource._flattenedData.subscribe( flatItemArray => {
      flatItemArray.forEach( flatItem => {
        this.flatItemArray = flatItemArray;
        // Selection of ONLY child items are considered 
        // as the parent item selection is automatically taken care 
        if(!flatItem.expandable && flatItem.isSelected){
          this.todoLeafItemSelectionToggle(flatItem);
        }
      })
    });
   
  }
  sendFormattedData(){
    const flatTree = [...this.flatItemArray];
    const deselected = flatTree.filter((item:FlatTreeNode) => {
      return !this.checklistSelection.isSelected(item)
    })
    const allData = {selected: this.checklistSelection.selected,deselected}
    this.selectedItems.emit(allData);
  }
   
   /** Whether all the descendants of the node are selected. */
   descendantsAllSelected(node: FlatTreeNode): boolean {
     const descendants = this.treeControl.getDescendants(node);
     const descAllSelected = descendants.every(child =>
       this.checklistSelection.isSelected(child)
     );
     return descAllSelected;
   }
 
   /** Whether part of the descendants are selected */
   descendantsPartiallySelected(node: FlatTreeNode): boolean {
     const descendants = this.treeControl.getDescendants(node);
     const result = descendants.some(child => this.checklistSelection.isSelected(child));
     return result && !this.descendantsAllSelected(node);
   }
   /** Toggle the to-do item selection. Select/deselect all the descendants node */
   mapItemSelectionToggle(node: FlatTreeNode): void {
     this.checklistSelection.toggle(node);
     const descendants = this.treeControl.getDescendants(node);
     this.checklistSelection.isSelected(node)
       ? this.checklistSelection.select(...descendants)
       : this.checklistSelection.deselect(...descendants);
 
     // Force update for the parent
     descendants.every(child =>
       this.checklistSelection.isSelected(child)
     );
     this.checkAllParentsSelection(node);
    this.sendFormattedData();
   }
  
   /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
   todoLeafItemSelectionToggle(node: FlatTreeNode): void {
     this.checklistSelection.toggle(node);
     this.checkAllParentsSelection(node);
     this.sendFormattedData();
   }
 
   /* Checks all the parents when a leaf node is selected/unselected */
   checkAllParentsSelection(node: FlatTreeNode): void {
     let parent: FlatTreeNode | null = this.getParentNode(node);
     while (parent) {
       this.checkRootNodeSelection(parent);
       parent = this.getParentNode(parent);
     }
   }
 
   /** Check root node checked state and change it accordingly */
   checkRootNodeSelection(node: FlatTreeNode): void {
     const nodeSelected = this.checklistSelection.isSelected(node);
     const descendants = this.treeControl.getDescendants(node);
     const descAllSelected = descendants.every(child =>
       this.checklistSelection.isSelected(child)
     );
     if (nodeSelected && !descAllSelected) {
       this.checklistSelection.deselect(node);
     } else if (!nodeSelected && descAllSelected) {
       this.checklistSelection.select(node);
     }
   }
 
   /* Get the parent node of a node */
   getParentNode(node: FlatTreeNode): FlatTreeNode | null {
     const currentLevel = this.getLevel(node);
 
     if (currentLevel < 1) {
       return null;
     }
 
     const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
     for (let i = startIndex; i >= 0; i--) {
       const currentNode = this.treeControl.dataNodes[i];
       if (this.getLevel(currentNode) < currentLevel) {
         return currentNode;
       }
     }
     return null;
   }
 

}
